import * as React from "react";
import { Flex, FlexProps } from "../flex";
import { ConditionalClassNameObject, cx } from "../../utils/dom";
import { SizeOption } from "../../utils/theme";
import { StyleProps } from "../../utils/types";

const SpaceContext = React.createContext<{
  classNames: ConditionalClassNameObject;
  compactItemWrapperAdjust: string;
}>({ classNames: {}, compactItemWrapperAdjust: "" });

type SpaceProps = FlexProps &
  StyleProps<"wrapper"> & {
    compact?: boolean;
    gap?: SizeOption;
  };

export function Space({
  children,
  compact,
  gap,
  className,
  classNames,
  style,
  styles,
  ...props
}: React.PropsWithChildren<SpaceProps>) {
  const childrenCount = React.Children.count(children);

  const nodes = React.useMemo(() => {
    return React.Children.map(children, (child, index) => {
      const isFirstChild = index == 0;
      const isLastChild = index == childrenCount - 1;

      if (!React.isValidElement(child)) return null;

      return (
        <SpaceContext.Provider
          value={{
            classNames: {
              "space-item-first": isFirstChild,
              "space-item-last": isLastChild,
              "space-item": true,
            },
            compactItemWrapperAdjust: isLastChild
              ? ""
              : "compact-item-wrapper-adjust",
          }}
        >
          {child}
        </SpaceContext.Provider>
      );
    });
  }, [children]);

  return (
    <Flex
      data-compact={compact}
      style={{ ...style, ...styles?.wrapper }}
      className={cx([className, "p-space", classNames?.wrapper])}
      gap={gap ?? "none"}
      {...props}
    >
      {nodes}
    </Flex>
  );
}

export function useSpace() {
  return React.useContext(SpaceContext);
}
